import React, { lazy, Suspense } from "react";
import { Text } from "web/react/emo/text";

import clsx from "clsx";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import * as styles from "./button.css";

const LoadingSpinnerAnimation = lazy(
    () =>
        import(
            "web/react/components/lottie-animations/loading-spinner-animation/loading-spinner-animation"
        )
);

export interface ButtonProps {
    className?: string;
    size?: "sm" | "lg";
    width?: "full" | "fit-content";
    icon?: SVGIconName;
    iconPosition?: "left" | "right";
    variant?: "primary" | "secondary" | "tertiary";
    active?: boolean;
    condensed?: boolean;
    inactive?: boolean;
    loading?: boolean;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    title: string;
    titleClassName?: string;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
    dataTestId?: string;
    ariaDescribedBy?: string;
}

/**
 * Design System
 * @name Button
 * @version 1.0
 * @design https://www.figma.com/file/VhOHx4tv9uYXZHXAAKPVYf/Design-System?type=design&node-id=3481-30566&t=sJmKH2VN0OACQRby-4
 */
export function Button({
    className,
    size = "lg",
    width = "fit-content",
    icon,
    iconPosition = "left",
    variant = "primary",
    active = false,
    inactive = false,
    loading = false,
    href,
    onClick,
    title,
    titleClassName,
    type = "button",
    dataTestId,
    condensed = false,
    ariaDescribedBy,
}: ButtonProps): React.ReactElement {
    const isInactive = inactive || loading;
    const buttonClass = styles.buttonRecipe({
        size,
        width,
        active,
        inactive,
        loading,
        variant,
        condensed,
        useButtonAnimation: true,
    });

    const content = (
        <div
            className={clsx(styles.container, loading && styles.loadingContent, {
                [styles.iconLeft]: iconPosition === "left",
                [styles.iconRight]: iconPosition === "right",
            })}
            data-testid={dataTestId}
        >
            {loading && (
                <div className={size === "sm" ? styles.loadingSpinnerSm : styles.loadingSpinner}>
                    <Suspense fallback={null}>
                        <LoadingSpinnerAnimation />
                    </Suspense>
                </div>
            )}
            {icon && !loading && (
                <SVGIcon
                    className={clsx(styles.icon, {
                        [styles.lystAppIcon]: icon === "lyst-app-icon" && variant === "primary",
                    })}
                    name={icon}
                    overrideStyle
                />
            )}
            <Text
                as="span"
                className={clsx(
                    titleClassName,
                    width === "full" && icon ? styles.fullWidth : undefined
                )}
                textStyle={size === "lg" ? "body-s-bold" : "body-s"}
                color={loading ? "grayscale0" : "inherit"}
            >
                {title}
            </Text>
        </div>
    );

    return href ? (
        <a
            className={clsx(className, buttonClass)}
            onClick={onClick}
            href={href}
            aria-describedby={ariaDescribedBy}
        >
            {content}
        </a>
    ) : (
        <button
            className={clsx(className, buttonClass)}
            onClick={onClick}
            disabled={isInactive}
            type={type}
            aria-describedby={ariaDescribedBy}
        >
            {content}
        </button>
    );
}
